import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useTextResources, useMessageResources } from '../../../language';
import { useConfirm } from '../../../hooks/useConfirm';
import { RateAssignment as RateAssignmentApi } from '../../../api/services/next';
import { EntityType, maxPromoCodeLength } from '../../../components/shared/constants';
import GridPanel from '../../../components/GridPanel';
import PropTypes from 'prop-types';
import useBoolean from '../../../hooks/useBoolean';
import useCallbackWithLoader from '../../../hooks/useCallbackWithLoader';
import useSafeState from "../../../hooks/useSafeState";
import { onWarning } from '../../../components/shared/messages';
import { Icon, Popup } from 'semantic-ui-react';
import TextInput from '../../../components/shared/TextInput';
import AddMultipleRateAssignmentsModal from "./popups/AddMultipleRateAssignmentsPopup";
import CustomerColumn from './grid/CustomerColumn';
import PromoCodeColumn from './grid/PromoCodeColumn';
import EditComissionsColumn from './grid/EditComissionsColumn';
import CustomerGroupColumn from './grid/CustomerGroupColumn';
import DiscountPercentColumn from './grid/DiscountPercentColumn';
import CustomerEditor from './grid/CustomerEditor';
import EditMultipleDiscountPercentPopup from './popups/EditMultipleDiscountPercentPopup';
import NumericInput from '../../../components/shared/NumericInput';

const DiscountPercentTitle = ({ selectedRows, filteredRowsObject, setIsMultipleDiscountEditOpen, isMultipleDiscountEditOpen, processChangedItems, text }) => {
    const isDisabled = selectedRows.length === 0;

    return (
        <>
            {isDisabled ? (
                <Popup
                    trigger={<Icon color='grey' className='pencil' />}
                    content={text('SelectOneCustomer')}
                />
            ) : (
                <EditMultipleDiscountPercentPopup
                    selectedRows={filteredRowsObject}
                    isOpen={isMultipleDiscountEditOpen}
                    saveDiscounts={processChangedItems}
                    setIsOpen={setIsMultipleDiscountEditOpen}
                />
            )}
            {text('DiscountPercent')}
        </>
    );
};

export const RateAssignments = ({ rateId }) => {
	const [selectedRows, setSelectedRows] = useState([]);
	const [isMultipleDiscountEditOpen, setIsMultipleDiscountEditOpen] = useState(false);

    const callWithLoader = useCallbackWithLoader();
    const text = useTextResources();
    const message = useMessageResources();
    const confirm  = useConfirm();

    const [rateAssignments, setRateAssignments] = useSafeState([]);
    const rateAssignmentsRows = useMemo(() => {
        return rateAssignments.reduce((result, rateAssignment) => {
            if (rateAssignment.CustomerGroupAssignments?.length) {
                rateAssignment.CustomerGroupAssignments.forEach((customerGroupAssignment) => {
                    result.push({
                        ...rateAssignment,
                        CustomerGroupAssignmentId: customerGroupAssignment.Id,
                        CustomerGroupId: customerGroupAssignment.CustomerGroup.Id,
                    });
                });
            }
            else {
                result.push({
                    ...rateAssignment,
                    CustomerGroupAssignmentId: undefined,
                    CustomerGroupId: undefined,
                });
            }
            return result;
        }, []);
    }, [rateAssignments]);
    const {
        value: isAddMultipleRateAssignmentsModalOpen,
        setTrue: openAddMultipleRateAssignmentsModal,
        setFalse: closeAddMultipleRateAssignmentsModal,
    } = useBoolean(false);

    const refreshRateAssignments = useCallback(async () => {
        const assignments = await RateAssignmentApi.GetRateAssignments({
            EntityTypeId: EntityType.Customer,
            RateId: rateId,
            SkipCache: false,
            includes: ['RateCustomerCommissions']
        }, {
            onErrorResult: [],
        });
        setRateAssignments(assignments);
    }, [rateId, setRateAssignments]);

    useEffect(() => {
        callWithLoader(refreshRateAssignments);
    }, [callWithLoader, refreshRateAssignments]);

    const validateAddedItems = useCallback((addedItems) => {
        const newPromoCodes = new Set(addedItems.filter(a => a.PromoCode).map(a => a.PromoCode));
        const newCustomerIds = new Set(addedItems.filter(a => a.EntityId).map(a => a.EntityId));

        const isDuplicatedPromoItem = rateAssignments.some(ra => newPromoCodes.has(ra.PromoCode));
        const isDuplicatedCustomer = rateAssignments.some(ra => newCustomerIds.has(ra.EntityId));
        const isEmptyCustomer = addedItems.some(a => !a.EntityId);

        const warnings = [
            { condition: isEmptyCustomer, message: 'CustomerCanNotBeEmpty' },
            { condition: isDuplicatedCustomer, message: 'CustomerAlreadyExists' },
            { condition: isDuplicatedPromoItem, message: 'PromoCodeAlreadyExists' },
        ];

        const warning = warnings.find(w => w.condition);
        return warning ? warning.message : null;
    }, [rateAssignments]);

    const validateItemsAfterChange = useCallback((rateAssignmentAfterChange) => {
        const allPromoCodes = rateAssignmentAfterChange.map(ra => ra.PromoCode).filter(Boolean);
        const uniquePromoCodes = [...new Set(allPromoCodes)];

        if (allPromoCodes.length !== uniquePromoCodes.length) {
            return 'PromoCodeAlreadyExists';
        }

        return null;
    }, []);


    const processAddedItems = useCallback(async (addedItems) => {
        const warningMessage = validateAddedItems(addedItems);

        if (warningMessage) {
            onWarning(message(warningMessage));
            return;
        }

        const rateAssignmentsToAdd = addedItems.map((item) => ({
            ...item,
            EntityTypeId: EntityType.Customer,
            RateId: rateId,
            RateCustomerCommissions: [],
        }));
        callWithLoader(async () => {
            await RateAssignmentApi.CreateOrUpdateRateAssignments(rateAssignmentsToAdd);
            await refreshRateAssignments();
        });
    }, [callWithLoader, message, rateId, refreshRateAssignments, validateAddedItems]);

    const processChangedItems = useCallback(async (changedKeyValue) => {
        const {
            rateAssignmentsToChange,
            rateAssignmentAfterChange,
        } = rateAssignmentsRows.reduce((acc, item, index) => {
            const rateAssignment = rateAssignments.find((ra) => ra.Id === item.Id);
            if (changedKeyValue[index]) {
                const changedRateAssignment = { ...rateAssignment, ...changedKeyValue[index] };
                acc.rateAssignmentsToChange.push(changedRateAssignment);
                acc.rateAssignmentAfterChange.push(changedRateAssignment);
            } else {
                acc.rateAssignmentAfterChange.push(rateAssignment);
            }
            return acc;
        }, {
            rateAssignmentsToChange: [],
            rateAssignmentAfterChange: [],
        });

        if (!rateAssignmentsToChange.length) return;

        const warningMessage = validateItemsAfterChange(rateAssignmentAfterChange);

        if (warningMessage) {
            onWarning(message(warningMessage));
            return;
        }
        callWithLoader(async () => {
            await RateAssignmentApi.CreateOrUpdateRateAssignments(rateAssignmentsToChange);
            await refreshRateAssignments();
        });
    }, [callWithLoader, message, rateAssignments, rateAssignmentsRows, refreshRateAssignments, validateItemsAfterChange]);

    const deleteRateAssignments = useCallback(async (deletedItemIndexes) => {
        const deleteIndexesSet = new Set(deletedItemIndexes);
        const customerGroupAssignmentToDeleteIdsSet = new Set();
        const rateAssignmentToDeleteIdsSet = new Set();
        rateAssignmentsRows.forEach((row, index) => {
            if (deleteIndexesSet.has(index)) {
                if (row.CustomerGroupAssignmentId) {
                    customerGroupAssignmentToDeleteIdsSet.add(row.CustomerGroupAssignmentId);
                } else {
                    rateAssignmentToDeleteIdsSet.add(row.Id);
                }
            }
        });
        callWithLoader(async () => {
                if (customerGroupAssignmentToDeleteIdsSet.size > 0) {
                    await RateAssignmentApi.DeleteCustomerGroupRateAssignments([...customerGroupAssignmentToDeleteIdsSet]);
                }
                if (rateAssignmentToDeleteIdsSet.size > 0) {
                    await RateAssignmentApi.DeleteRateAssignments([...rateAssignmentToDeleteIdsSet]);
                }
                await refreshRateAssignments();
            }, {
            showSuccessMessage: true,
        });
    }, [callWithLoader, rateAssignmentsRows, refreshRateAssignments]);

    const processDeletedItems = useCallback(async (deletedItemIndexes) => {
        await confirm({
            header: message('AreYouSure'),
            content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
            confirmButton: {
                content: text('Yes'),
                positive: true,
            },
            cancelButton: {
                content: text('No'),
                negative: true,
            },
            onConfirm: async () => deleteRateAssignments(deletedItemIndexes),
        });
    }, [confirm, deleteRateAssignments, message, text]);

    const onGridUpdate = useCallback(async (added, changed, deleted) => {
        if (added?.length) {
            await processAddedItems(added);
        } else if (changed) {
            await processChangedItems(changed);
        } else if (deleted?.length) {
            await processDeletedItems(deleted);
        }
    }, [processDeletedItems, processAddedItems, processChangedItems]);

    const createMultipleRateAssignments = useCallback(async (spec) => {
        callWithLoader(async () => {
            await RateAssignmentApi.CreateMultipleRateAssignments(spec);
            await refreshRateAssignments();
            closeAddMultipleRateAssignmentsModal();
        }, {
            showSuccessMessage: true,
        });
    }, [callWithLoader, closeAddMultipleRateAssignmentsModal, refreshRateAssignments]);

    const customerPredicate = useCallback((customerId, filter) => {
        const customerName = rateAssignments.find(r => r.EntityId === customerId)?.CustomerName.toLowerCase() ?? '';
        return customerName.includes(filter.value.toLowerCase());
    }, [rateAssignments]);


	const onSelectRows = (selected) => {
		setSelectedRows(selected);
	}

	const filteredRowsObject = useMemo(() => {
		return rateAssignmentsRows.reduce((result, assignment, i) => {
			if(selectedRows.includes(i)) {
				result[i] = rateAssignmentsRows.find(r => r.Id === assignment.Id);
			}
			return result;
		}, {});
	}, [selectedRows, rateAssignmentsRows]);


    const gridOptions = useMemo(() => ({
        columns: [
            {
                name: 'CustomerGroupId',
                title: text('CustomerGroup'),
                formatter: CustomerGroupColumn,
                rows: rateAssignmentsRows,
            },
            {
                name: 'EntityId',
                title: text('Customer'),
                formatter: CustomerColumn,
                editor: <CustomerEditor rateId={rateId} />,
                rows: rateAssignmentsRows,
            },
            {
                name: 'PromoCode',
                title: text('PromoCode'),
                formatter: PromoCodeColumn,
                editor: <TextInput placeholder={text('PromoCode')} maxLength={maxPromoCodeLength} />,
                rows: rateAssignmentsRows,
            },
			{
                name: 'DiscountPercent',
				title: (
                    <DiscountPercentTitle 
                        selectedRows={selectedRows}
                        filteredRowsObject={filteredRowsObject}
                        setIsMultipleDiscountEditOpen={setIsMultipleDiscountEditOpen}
                        isMultipleDiscountEditOpen={isMultipleDiscountEditOpen}
                        processChangedItems={processChangedItems}
                        text={text}
                    />
                ),
                formatter: DiscountPercentColumn,
				editor: <NumericInput placeholder={text('DiscountPercent')} min={0} max={100} digits={2} nullable={true} />,
                rows: rateAssignmentsRows,
            },
            {
                name: 'RateCustomerCommissions',
                title: text('Commissions'),
                formatter: EditComissionsColumn,
                rateId,
                refreshRateAssignments,
                rows: rateAssignmentsRows,
            },
        ],
        editable: {
            allowAdd: true,
            allowEdit: true,
        },
        cacheId: 'RateAssignments',
        paging: true,
        selectable: true,
        filtering: {
            extensions: [
                { columnName: 'EntityId', predicate: customerPredicate },
                { columnName: 'RateCustomerCommissions', filteringEnabled: false },
            ],
        },
        showFilter: true,
        showDeleteMultiple: true,
        columnExtensions: [
            { columnName: 'CustomerGroupId', editingEnabled: false },
            { columnName: 'EntityId', editingEnabled: false },
            { columnName: 'RateCustomerCommissions', editingEnabled: false },
        ],
        customOptions: {
            sort: {},
            group: {
                grouping: [{ columnName: 'CustomerGroupId' }],
                extensions: [
                    { columnName: 'CustomerGroupId', groupingEnabled: false },
                ]
            },
        },
        menu: [
            <Popup
                content='Add Multiple Customers'
                position='bottom center'
                mouseEnterDelay={300}
                key="AddMultiple"
                trigger={
                    <Icon
                        name='add'
                        link
                        onClick={openAddMultipleRateAssignmentsModal}
                    />
                }
            />
          ]
    }), [text, rateAssignmentsRows, rateId, selectedRows, filteredRowsObject, isMultipleDiscountEditOpen, processChangedItems, refreshRateAssignments, customerPredicate, openAddMultipleRateAssignmentsModal]);

    return (
        <div className='assignments-grid-wrapper'>
            <GridPanel
                data={rateAssignmentsRows}
                options={gridOptions}
                onUpdate={onGridUpdate}
				onSelected={onSelectRows}
            />
            <AddMultipleRateAssignmentsModal
                open={isAddMultipleRateAssignmentsModalOpen}
                onClose={closeAddMultipleRateAssignmentsModal}
                rateId={rateId}
                createMultipleRateAssignments={createMultipleRateAssignments}
            />
        </div>
    );
};

RateAssignments.propTypes = {
    rateId: PropTypes.number,
};

DiscountPercentTitle.propTypes = {
    selectedRows: PropTypes.array,
    filteredRowsObject: PropTypes.object,
    setIsMultipleDiscountEditOpen: PropTypes.func,
    isMultipleDiscountEditOpen: PropTypes.bool, 
    processChangedItems: PropTypes.func,
    text: PropTypes.func
};
